import { Box, Stack, Switch, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { NOTIFICATION_CATEGORIES, type NotificationCategoryId } from '@serb/shared';
import { Panel } from '../../components/Panel';
import { StatusChip } from '../../components/StatusChip';
import { toneColor } from '../../components/tone';
import { jordanDate } from '@serb/shared';
import { shortDate } from '../../lib/dates';
import { useAction } from '../../lib/useAction';
import { setSettings, useSettings } from '../../lib/settingsStore';
import { useApprovalCount } from '../approvals/useApprovals';
import { settingsApi } from '../settings/api';
import type { ReminderItem } from '../reminders/model';
import { statusOf } from '../reminders/model';
import { nextWeek, type Notif } from './build';

/** Four numbers: everything, what needs a decision, what is urgent, what is news */
export function SummaryPanel({ items }: { items: Notif[] }) {
  const stat = [
    { label: 'Total Notifications', n: items.length },
    { label: 'Needs Action', n: items.filter((i) => i.group === 'Needs Attention').length },
    { label: 'Urgent', n: items.filter((i) => i.priority === 'High').length },
    { label: 'Updates', n: items.filter((i) => i.group === 'Updates').length },
  ];
  return (
    <Panel title="Notification Summary">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
        {stat.map((s) => (
          <Box key={s.label} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 26, lineHeight: 1 }}>{s.n}</Typography>
            <Typography variant="caption" color="text.secondary">
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Panel>
  );
}

/** Where to start today: approvals waiting, shipments with news, reminders due */
export function TodaysFocusPanel({ items, reminders }: { items: Notif[]; reminders: ReminderItem[] }) {
  const today = jordanDate();
  const approvals = useApprovalCount();
  const rows = [
    { n: approvals, label: 'Approvals pending', to: '/approvals' },
    {
      n: items.filter((i) => i.category === 'Shipment Updates').length,
      label: 'Shipments with updates',
      to: '/shipments',
    },
    {
      n: reminders.filter((r) => !r.complete && r.date === today).length,
      label: 'Reminders due today',
      to: '/reminders',
    },
  ];
  return (
    <Panel title="Today's Focus">
      <Stack spacing={0.5}>
        {rows.map((r) => (
          <Stack
            key={r.label}
            component={RouterLink}
            to={r.to}
            direction="row"
            spacing={1.5}
            sx={{ alignItems: 'center', p: 0.75, color: 'inherit', textDecoration: 'none', borderRadius: 1.5 }}
          >
            <Typography sx={{ fontWeight: 800, width: 24 }}>{r.n}</Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {r.label}
            </Typography>
            <ChevronRight fontSize="small" color="disabled" />
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
}

/** Reminders in the next 7 days, soonest first */
export function UpcomingDeadlinesPanel({ reminders }: { reminders: ReminderItem[] }) {
  const today = jordanDate();
  const rows = nextWeek(reminders).slice(0, 5);
  return (
    <Panel title="Upcoming Deadlines" viewAll="/reminders">
      {rows.length ? (
        <Stack spacing={1}>
          {rows.map((r) => {
            const s = statusOf(r, today);
            return (
              <Stack key={r.id} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 2, bgcolor: toneColor(s.tone) }} />
                <Typography variant="body2" sx={{ width: 52, fontWeight: 600, flexShrink: 0 }}>
                  {r.date === today ? 'Today' : shortDate(r.date).split(' ').slice(0, 2).join(' ')}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                  {r.title}
                  {r.srb ? ` (${r.srb})` : ''}
                </Typography>
                <StatusChip label={s.label} tone={s.tone} />
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nothing due in the next 7 days.
        </Typography>
      )}
    </Panel>
  );
}

/** Which kinds of notification are shown. Saved with the other settings, so your Mac and iPhone agree. */
export function NotificationSettingsPanel() {
  const settings = useSettings();
  const save = useAction(settingsApi.save, { onDone: (s) => setSettings(s) });
  const toggle = (id: NotificationCategoryId, on: boolean) =>
    save.run({ ...settings, notifications: { ...settings.notifications, [id]: on } });
  return (
    <Panel
      title="Notification Settings"
      subtitle="Choose which kinds of notification you see. Phone and Mac alerts are set below."
    >
      <Stack spacing={0.5}>
        {NOTIFICATION_CATEGORIES.map((c) => (
          <Stack key={c.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {c.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {c.help}
              </Typography>
            </Box>
            <Switch
              checked={settings.notifications[c.id]}
              disabled={save.busy}
              onChange={(e) => toggle(c.id, e.target.checked)}
              slotProps={{ input: { 'aria-label': c.label } }}
            />
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
}
